import { useCallback } from 'react';
import type { ElasticListVehicle } from '@//app/api/search/vehicles';
import type { ElasticVehicle } from '@//app/api/search/vehicles/[id]';
import type { Market } from '@//config/domain-locale-map';
import { useLegacyTracking } from '@//providers/LegacyTrackingProvider';
import { useMarket } from '@//providers/MarketSettingsProvider';
import type { LegacyTrackSaveEventData } from './legacy-types';
import { useLegacyTrackShownInterest } from './useLegacyTrackShownInterest';
import { mapElasticVehicleOrElasticListVehicleToLegacyItem } from './utils';

export interface UseLegacyTrackSaveVariables {
  vehicle: ElasticVehicle | ElasticListVehicle;
  pageType: LegacyTrackSaveEventData['pageType'];
}

export const createLegacyTrackSave = (
  market: Market,
  variables: UseLegacyTrackSaveVariables,
): LegacyTrackSaveEventData => {
  const eventData: LegacyTrackSaveEventData = {
    eventType: 'microConversion',
    conversionType: 'save',
    item: mapElasticVehicleOrElasticListVehicleToLegacyItem(market, variables.vehicle),
  };

  if ('id' in variables.vehicle && variables.vehicle.id) {
    eventData.pageType = 'product page';
  }

  return eventData;
};

export const useLegacyTrackSave = () => {
  const market = useMarket();
  const { track } = useLegacyTracking();
  const { trackLegacyShownInterest } = useLegacyTrackShownInterest();

  const trackLegacySave = useCallback(
    (variables: UseLegacyTrackSaveVariables) => {
      trackLegacyShownInterest(variables.vehicle);
      track({
        event: 'microConversion',
        data: createLegacyTrackSave(market, variables),
      });
    },
    [market, track],
  );

  return {
    trackLegacySave,
  };
};
